import React from "react";
import {
  AbsoluteFill,
  Img,
  Audio,
  Sequence,
  staticFile,
  useCurrentFrame,
  interpolate,
  Easing,
  random,
} from "remotion";

/**
 * PaperRipReveal — 30 images, 17s @ 16fps @ 1280x720
 *
 * Each image holds briefly, then TEARS APART into two halves that slide
 * in opposite directions, revealing the next image underneath. Mixed seam
 * directions (vertical center/left/right, horizontal center/top/bottom),
 * with real edge_*.png sprites painted along the moving seam. Occasional
 * "punch" slots zoom+RGB-split instead. Final tear reveals the title card.
 */

const FPS = 16;
const W = 1280;
const H = 720;
const PER_SLOT = 8;       // cadence — new image every 0.5s
const TRANSITION = 4;     // tear duration — 0.25s
const N = 30;             // total images
const TAIL = 32;          // final title-card hold (2s)
const TOTAL = N * PER_SLOT + TAIL; // 272 frames = 17s

type Dir = "VC" | "VL" | "VR" | "HC" | "HT" | "HB" | "PUNCH";

interface Slot {
  src: string;
  dir: Dir;
  edgeIdx: number;       // 1..21
  sfxIdx: number;        // 0..9
  kbX: number;
  kbY: number;
  slideAmt: number;      // how far halves fly
  rotAmt: number;        // rotation of halves
}

const PUNCH_RATE = 0.10;
const DIR_POOL: Dir[] = ["VC", "VL", "VR", "HC", "HT", "HB", "VC", "VL", "HC"];

function buildSlots(): Slot[] {
  return Array.from({ length: N }, (_, i) => {
    const r = (k: string) => random(`s${i}-${k}`);
    const isPunch = r("p") < PUNCH_RATE && i > 0 && i < N - 1;
    const dir: Dir = isPunch ? "PUNCH" : DIR_POOL[Math.floor(r("d") * DIR_POOL.length)];
    return {
      src: staticFile(`trimbak/img_${(i % 34).toString().padStart(2, "0")}.jpg`),
      dir,
      edgeIdx: 1 + Math.floor(r("e") * 21),
      sfxIdx: i % 10,
      kbX: (r("kx") - 0.5) * 0.8,
      kbY: (r("ky") - 0.5) * 0.8,
      slideAmt: 0.45 + r("sa") * 0.25,
      rotAmt: (r("ra") - 0.5) * 16,
    };
  });
}

const SLOTS = buildSlots();

// ─── Jagged seam generators ──────────────────────────────────────────────────
function jaggedSeamV(seamX: number, jagged: number, seed: number) {
  const teeth = 22;
  const pts: { x: number; y: number }[] = [{ x: seamX, y: 0 }];
  for (let i = 1; i < teeth; i++) {
    const yv = (i / teeth) * H;
    const off = (random(`vs-${seed}-${i}`) - 0.5) * 2 * jagged;
    pts.push({ x: seamX + off, y: yv });
  }
  pts.push({ x: seamX, y: H });
  return pts;
}

function jaggedSeamH(seamY: number, jagged: number, seed: number) {
  const teeth = 28;
  const pts: { x: number; y: number }[] = [{ x: 0, y: seamY }];
  for (let i = 1; i < teeth; i++) {
    const xv = (i / teeth) * W;
    const off = (random(`hs-${seed}-${i}`) - 0.5) * 2 * jagged;
    pts.push({ x: xv, y: seamY + off });
  }
  pts.push({ x: W, y: seamY });
  return pts;
}

function polyPath(pts: { x: number; y: number }[]) {
  return `polygon(${pts.map((p) => `${p.x}px ${p.y}px`).join(", ")})`;
}

// ─── Torn-halves renderer for a tearing-out slot ─────────────────────────────
const TornHalves: React.FC<{
  slot: Slot;
  progress: number;
  seed: number;
  kbX: number;
  kbY: number;
  kbScale: number;
}> = ({ slot, progress, seed, kbX, kbY, kbScale }) => {
  const dir = slot.dir;
  const edgeUrl = staticFile(`paper_rip/edge_${slot.edgeIdx}.png`);
  const JAGGED = 22;

  let polyA: { x: number; y: number }[] = [];
  let polyB: { x: number; y: number }[] = [];
  let transformA = "";
  let transformB = "";
  let edgeStyleA: React.CSSProperties = {};
  let edgeStyleB: React.CSSProperties = {};

  if (dir === "VC" || dir === "VL" || dir === "VR") {
    const seamX = dir === "VC" ? W / 2 : dir === "VL" ? W * 0.36 : W * 0.64;
    const seam = jaggedSeamV(seamX, JAGGED, seed);
    polyA = [{ x: 0, y: 0 }, ...seam, { x: 0, y: H }];
    polyB = [{ x: W, y: 0 }, ...seam, { x: W, y: H }];
    const slide = progress * slot.slideAmt * W;
    transformA = `translateX(${-slide}px) rotate(${-progress * slot.rotAmt}deg)`;
    transformB = `translateX(${slide}px) rotate(${progress * slot.rotAmt}deg)`;
    edgeStyleA = {
      position: "absolute",
      left: seamX - 28,
      top: 0,
      width: 56,
      height: H,
      transform: "scaleX(-1)",
      pointerEvents: "none",
    };
    edgeStyleB = {
      position: "absolute",
      left: seamX - 28,
      top: 0,
      width: 56,
      height: H,
      pointerEvents: "none",
    };
  } else {
    // HC / HT / HB
    const seamY = dir === "HC" ? H / 2 : dir === "HT" ? H * 0.36 : H * 0.64;
    const seam = jaggedSeamH(seamY, JAGGED, seed);
    polyA = [{ x: 0, y: 0 }, ...seam, { x: W, y: 0 }];
    polyB = [{ x: 0, y: H }, ...seam, { x: W, y: H }];
    const slide = progress * slot.slideAmt * H;
    transformA = `translateY(${-slide}px) rotate(${-progress * slot.rotAmt}deg)`;
    transformB = `translateY(${slide}px) rotate(${progress * slot.rotAmt}deg)`;
    edgeStyleA = {
      position: "absolute",
      left: 0,
      top: seamY - 28,
      width: W,
      height: 56,
      transform: "rotate(180deg)",
      pointerEvents: "none",
    };
    edgeStyleB = {
      position: "absolute",
      left: 0,
      top: seamY - 28,
      width: W,
      height: 56,
      pointerEvents: "none",
    };
  }

  const opacity = 1 - Math.pow(progress, 1.4) * 0.7;

  return (
    <AbsoluteFill style={{ zIndex: 200, opacity }}>
      {/* Half A */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          clipPath: polyPath(polyA),
          WebkitClipPath: polyPath(polyA),
          transform: transformA,
          willChange: "transform",
        }}
      >
        <Img
          src={slot.src}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: `translate(${kbX}px,${kbY}px) scale(${kbScale})`,
          }}
        />
        <img src={edgeUrl} style={edgeStyleA} />
      </div>
      {/* Half B */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          clipPath: polyPath(polyB),
          WebkitClipPath: polyPath(polyB),
          transform: transformB,
          willChange: "transform",
        }}
      >
        <Img
          src={slot.src}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: `translate(${kbX}px,${kbY}px) scale(${kbScale})`,
          }}
        />
        <img src={edgeUrl} style={edgeStyleB} />
      </div>
    </AbsoluteFill>
  );
};

// ─── Punch (zoom + RGB split flash) ──────────────────────────────────────────
const PunchOut: React.FC<{
  slot: Slot;
  progress: number;
  kbX: number;
  kbY: number;
  kbScale: number;
}> = ({ slot, progress, kbX, kbY, kbScale }) => {
  const punchScale = interpolate(progress, [0, 1], [1.0, 1.42]);
  const splitPx = progress * 18;
  const opacity = 1 - progress * 0.95;
  return (
    <AbsoluteFill style={{ zIndex: 200, opacity }}>
      <Img
        src={slot.src}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: `translate(${kbX - splitPx}px, ${kbY}px) scale(${kbScale * punchScale})`,
          mixBlendMode: "screen",
          opacity: 0.6,
        }}
      />
      <Img
        src={slot.src}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: `translate(${kbX + splitPx}px, ${kbY}px) scale(${kbScale * punchScale})`,
          filter: "hue-rotate(180deg)",
          mixBlendMode: "screen",
          opacity: 0.4,
        }}
      />
      <Img
        src={slot.src}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: `translate(${kbX}px, ${kbY}px) scale(${kbScale * punchScale})`,
        }}
      />
    </AbsoluteFill>
  );
};

// ─── Single slot (hold then tear-out) ────────────────────────────────────────
const SlotElement: React.FC<{ slot: Slot; index: number }> = ({ slot, index }) => {
  const t = useCurrentFrame();
  const kbScale = interpolate(t, [0, PER_SLOT + TRANSITION], [1.0, 1.07], {
    extrapolateRight: "clamp",
  });
  const kbX = interpolate(t, [0, PER_SLOT + TRANSITION], [0, slot.kbX * 28]);
  const kbY = interpolate(t, [0, PER_SLOT + TRANSITION], [0, slot.kbY * 18]);

  if (t < PER_SLOT) {
    // HOLD: full image with Ken Burns + vignette
    return (
      <AbsoluteFill style={{ zIndex: 100 }}>
        <Img
          src={slot.src}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: `translate(${kbX}px, ${kbY}px) scale(${kbScale})`,
          }}
        />
        <AbsoluteFill
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%)",
            pointerEvents: "none",
          }}
        />
      </AbsoluteFill>
    );
  }

  const tearT = t - PER_SLOT;
  const progress = interpolate(tearT, [0, TRANSITION], [0, 1], {
    extrapolateRight: "clamp",
    easing: Easing.in(Easing.cubic),
  });

  if (slot.dir === "PUNCH") {
    return <PunchOut slot={slot} progress={progress} kbX={kbX} kbY={kbY} kbScale={kbScale} />;
  }
  return (
    <TornHalves
      slot={slot}
      progress={progress}
      seed={index}
      kbX={kbX}
      kbY={kbY}
      kbScale={kbScale}
    />
  );
};

// ─── SFX layer ───────────────────────────────────────────────────────────────
const SfxLayer: React.FC = () => (
  <>
    {SLOTS.map((s, i) => (
      <Sequence
        key={`sfx-${i}`}
        from={i * PER_SLOT + PER_SLOT - 1}
        durationInFrames={TRANSITION + 6}
      >
        <Audio
          src={staticFile(
            `paper_rip/sfx/${(s.sfxIdx + 1).toString().padStart(2, "0")} -- Edge Fast.wav`,
          )}
          volume={0.55}
        />
      </Sequence>
    ))}
  </>
);

// ─── Title card (revealed by the final tear) ─────────────────────────────────
const TitleCard: React.FC = () => {
  const frame = useCurrentFrame();
  const start = N * PER_SLOT - TRANSITION; // appears underneath last tear
  const local = frame - start;
  if (local < 0) return null;
  const op = interpolate(local, [0, 6, TAIL + TRANSITION - 8, TAIL + TRANSITION], [0, 1, 1, 0.85], {
    extrapolateRight: "clamp",
  });
  const sc = interpolate(local, [0, 10], [1.18, 1.0], {
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  return (
    <AbsoluteFill
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50, // BELOW slot zIndex 100/200 — revealed only when last slot tears
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          padding: "26px 56px",
          background: "rgba(15,9,5,0.85)",
          border: "2px solid rgba(255,210,140,0.45)",
          borderRadius: 4,
          transform: `scale(${sc})`,
          opacity: op,
          boxShadow:
            "0 18px 60px rgba(0,0,0,0.88), 0 0 80px rgba(255,150,60,0.3)",
        }}
      >
        <div
          style={{
            fontFamily:
              "'Tiro Devanagari Hindi', 'Noto Serif Devanagari', serif",
            fontSize: 88,
            fontWeight: 900,
            color: "#ffe4b3",
            letterSpacing: 2,
            textShadow:
              "0 4px 24px rgba(0,0,0,0.95), 0 0 50px rgba(255,170,80,0.5)",
            lineHeight: 1.0,
            textAlign: "center",
          }}
        >
          त्र्यंबकेश्वर
        </div>
        <div
          style={{
            fontFamily: "Helvetica, Arial, sans-serif",
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: 6,
            color: "rgba(255,224,180,0.8)",
            textAlign: "center",
            marginTop: 10,
          }}
        >
          THE TWELFTH JYOTIRLINGA
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─── End fade ────────────────────────────────────────────────────────────────
const EndFade: React.FC = () => {
  const f = useCurrentFrame();
  const opacity = interpolate(f, [TOTAL - 12, TOTAL], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  if (opacity <= 0) return null;
  return (
    <AbsoluteFill
      style={{
        background: "#000",
        opacity,
        pointerEvents: "none",
        zIndex: 99999,
      }}
    />
  );
};

// ─── Main composition ────────────────────────────────────────────────────────
export const PaperRipReveal: React.FC = () => {
  return (
    <AbsoluteFill style={{ background: "#000" }}>
      <Audio src={staticFile("suspense_intro_17s.mp3")} />
      <SfxLayer />

      <TitleCard />

      {SLOTS.map((slot, i) => (
        <Sequence
          key={i}
          from={i * PER_SLOT}
          durationInFrames={PER_SLOT + TRANSITION}
        >
          <SlotElement slot={slot} index={i} />
        </Sequence>
      ))}

      {/* Film grain on top of everything except end fade */}
      <AbsoluteFill
        style={{
          background:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.012) 0 1px, transparent 1px 3px)",
          mixBlendMode: "overlay",
          pointerEvents: "none",
          zIndex: 5000,
        }}
      />

      <EndFade />
    </AbsoluteFill>
  );
};

export default PaperRipReveal;

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
